import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import PropertyValue from 'Components/Personal/Property/PropertyValue';
import NewProperty from 'Components/Personal/Property/NewProperty';
import { setPropertyInCard } from 'store/slices/personalSlice';
import { extractErrorMessage } from 'Modules/ParseAPI';
import './CardProperty.css'; // Cache bust: 2026-06-18-09:55 - Trying to force webpack reload

console.log('--- CARD PROPERTY V2 LOADED ---'); // Custom switch component to replace ReactSwitch and prevent memory leaks

function CustomSwitch({
  checked,
  onChange,
  disabled,
  ...props
}) {
  const {
    // eslint-disable-next-line no-unused-vars
    t
  } = useTranslation();
  const handleClick = useCallback(() => {
    if (!disabled && onChange) {
      onChange(!checked);
    }
  }, [checked, onChange, disabled]);
  return <div className={`hmi-custom-switch ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}`} onClick={handleClick}>
            <div className="switch-handle" />
        </div>;
}

function CardProperty({
  propertyMeta,
  card,
  setPropertyInCard,
  profile
}) {
  const {
    t
  } = useTranslation();
  const isMountedRef = useRef(true);
  const onChangeRef = useRef(null);
  const propertyName = propertyMeta ? propertyMeta.name : undefined;
  const propertyObjectId = propertyMeta ? propertyMeta.objectId : undefined;
  console.log(`CardProperty ${propertyName}: received card prop:`, card); // Always derive the latest card from Redux profile by id to avoid stale props

  const liveCard = useMemo(() => {
    const safeMapProperties = props => {
      if (!props || !Array.isArray(props)) return [];
      return props.map(p => p && p.objectId ? p.objectId : undefined);
    };

    console.log(`CardProperty ${propertyName}: Available cards:`, profile && profile.cards ? profile.cards.map(c => c ? {
      objectId: c.objectId,
      name: c.name,
      propertiesCount: c.properties ? c.properties.length : 0,
      properties: safeMapProperties(c.properties)
    } : null) : null); // Try to find the card by the original objectId or id first

    let c = null;
    const cardIdToMatch = card ? card.objectId || card.id : undefined;
    console.log(`CardProperty ${propertyName}: Looking for card with ID:`, cardIdToMatch);

    if (profile && profile.cards && cardIdToMatch) {
      c = profile.cards.find(c => c && (c.objectId === cardIdToMatch || c.id === cardIdToMatch));
    } // If not found, try to map short card ID to canonical card ID


    if (!c && card && card.objectId === 'E8BUJEjh9m') {
      // Map short Work card ID to canonical Work card ID
      if (profile && profile.cards) {
        c = profile.cards.find(c => c && c.objectId === 'card_work_KY5WuKOFkj');
        console.log(`CardProperty ${propertyName}: Mapped short card ID ${card.objectId} to canonical card ID card_work_KY5WuKOFkj`);
      }
    } // Fallback to original card if still not found


    if (!c) {
      c = card;
      console.log(`CardProperty ${propertyName}: Using fallback card`);
    }

    console.log(`CardProperty ${propertyName}: liveCard properties count:`, c && c.properties ? c.properties.length : 0);
    console.log(`CardProperty ${propertyName}: liveCard has property ${propertyObjectId}:`, c && c.properties ? c.properties.some(p => p && p.objectId === propertyObjectId) : false);
    return c;
  }, [profile, card, propertyName, propertyObjectId]);
  const cardProperties = useMemo(() => {
    if (!liveCard || !liveCard.properties) {
      return new Map();
    }

    return new Map(liveCard.properties.filter(Boolean).map(property => [property.objectId, property]));
  }, [liveCard]);
  const hasValue = useMemo(() => {
    if (!propertyMeta) return false;

    const normalizeStr = val => typeof val === 'string' ? val.trim() : val; // 1) Check direct scalar value or nested { value } shapes


    const direct = normalizeStr(propertyMeta.value);
    const nested = normalizeStr(propertyMeta.value ? propertyMeta.value.value : undefined);

    // eslint-disable-next-line no-mixed-operators
    if (typeof direct !== 'undefined' && direct !== '' && direct !== null || typeof nested !== 'undefined' && nested !== '' && nested !== null) {
      return true;
    } // 2) Multiple values array with possible nested shapes and/or parameters


    if (propertyMeta.multiple && Array.isArray(propertyMeta.values) && propertyMeta.values.length > 0) {
      return propertyMeta.values.some(v => {
        const vDirect = normalizeStr(v ? v.value : undefined);
        const vNested = normalizeStr(v && v.value ? v.value.value : undefined);
        const hasParamVal = v && v.parameters && Object.values(v.parameters).some(p => normalizeStr(p ? p.value : undefined));
        return vDirect || vNested || hasParamVal;
      });
    } // 3) Parameters object on single value properties


    if (propertyMeta.parameters && Object.values(propertyMeta.parameters).some(p => normalizeStr(p ? p.value : undefined))) {
      return true;
    }

    return false;
  }, [propertyMeta]);
  const [checked, setChecked] = useState(cardProperties.has(propertyObjectId));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isRequiredProperty = ['givenName', 'familyName'].includes(propertyName);
  useEffect(() => {
    let newChecked = cardProperties.has(propertyObjectId);

    if (isRequiredProperty) {
      newChecked = true;
    }

    console.log(`CardProperty ${propertyName}: cardProperties.has(${propertyObjectId}) = ${newChecked}`);
    console.log('cardProperties keys:', Array.from(cardProperties.keys()));
    setChecked(newChecked);
  }, [cardProperties, propertyObjectId, propertyName, profile, isRequiredProperty]);
  useLayoutEffect(() => {
    return () => {
      isMountedRef.current = false;
      onChangeRef.current = null;
    };
  }, []);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      onChangeRef.current = null;
    };
  }, []);
  const liveCardObjectId = liveCard ? liveCard.objectId || liveCard.id : undefined;
  const onChange = useCallback(nextChecked => {
    console.log(`CardProperty ${propertyName}: onChange called with ${nextChecked}`);
    if (!isMountedRef.current) return; // Prevent turning off both givenName and familyName

    if (!nextChecked && (propertyName === 'givenName' || propertyName === 'familyName')) {
      const otherName = propertyName === 'givenName' ? 'familyName' : 'givenName';
      const hasOtherName = liveCard && liveCard.properties && liveCard.properties.some(p => p.name === otherName);

      if (!hasOtherName) {
        setError('At least one of the firstname or lastname must remain on.');
        return;
      }
    } // Prevent turning off the last email or phone


    const isEmailOrPhone = ['emailAddresses', 'phoneNumbers', 'email', 'phone'].includes(propertyName);

    if (!nextChecked && isEmailOrPhone) {
      const enabledEmailPhoneCount = liveCard && liveCard.properties ? liveCard.properties.filter(p => p && ['emailAddresses', 'phoneNumbers', 'email', 'phone'].includes(p.name)).length : 0;

      if (enabledEmailPhoneCount <= 1) {
        setError('At least one email address or phone number must remain enabled on the card.');
        return;
      }
    }

    setError('');
    setChecked(nextChecked);
    setLoading(true);

    if (!liveCardObjectId) {
      console.error(`CardProperty ${propertyName}: Cannot set property, liveCard has no objectId`);

      if (isMountedRef.current) {
        setError('Card not found');
        setLoading(false);
      }

      return;
    }

    console.log(`CardProperty ${propertyName}: calling setPropertyInCard with cardId: ${liveCardObjectId}, propertyId: ${propertyObjectId}, add: ${nextChecked}`);
    setPropertyInCard(liveCardObjectId, propertyObjectId, nextChecked).then(() => {
      console.log(`CardProperty ${propertyName}: setPropertyInCard completed successfully`);
      if (isMountedRef.current) setLoading(false);
    }).catch(err => {
      console.error(`CardProperty ${propertyName}: setPropertyInCard error:`, err);

      if (isMountedRef.current) {
        setError(extractErrorMessage(err));
        setLoading(false);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCardObjectId, propertyObjectId, setPropertyInCard, propertyName]);
  useEffect(() => {
    console.log(`CardProperty ${propertyName}: Setting onChangeRef.current to onChange function`);
    onChangeRef.current = onChange;
  }, [onChange, propertyName]);
  const finalOnChange = useCallback(nextChecked => {
    console.log(`CardProperty ${propertyName}: finalOnChange called with ${nextChecked}`);
    if (!isMountedRef.current) return; // Call onChange directly instead of using ref

    console.log(`CardProperty ${propertyName}: calling onChange directly`);
    onChange(nextChecked);
  }, [onChange, propertyName]);
  return <div className='modern-property-control'>
            <div className='property-info'>
                <div className='property-name'>{t(propertyMeta.showName)}</div>
                {hasValue ? <div className='property-value'>
                        <PropertyValue propertyMeta={propertyMeta} value={propertyMeta.multiple && propertyMeta.values ? propertyMeta.values[0] : propertyMeta.value} />
                    </div> : <div className='property-empty'>
                        <NewProperty property={propertyMeta} />
                        <small className='empty-hint'>{t("Add a value to enable this on the card")}</small>
                    </div>}
                {propertyMeta.verified && <div className='verification-badge'>
                        <span className='verified-icon'>✓</span>{t("Verified")}</div>}
            </div>
            <div className='property-toggle'>
                {/* eslint-disable-next-line no-mixed-operators */}
                <CustomSwitch checked={checked} onChange={finalOnChange} disabled={loading || (!hasValue && !checked) || isRequiredProperty} />
                <div className='toggle-label'>
                    {checked ? t('Visible') : t('Hidden')}
                </div>
                {error && <div className='error-message'>{t(error)}</div>}
            </div>
        </div>;
}

const mapStateToProps = state => ({
  profile: state.auth.currentUser ? state.auth.currentUser.profile : undefined
});

const mapDispatchToProps = dispatch => ({
  setPropertyInCard: (cardId, propertyId, add) => dispatch(setPropertyInCard(cardId, propertyId, add))
});

export default connect(mapStateToProps, mapDispatchToProps)(CardProperty);